import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  Box, Typography, Grid, Card, CardContent, Button, Stack, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  LinearProgress, Alert, IconButton, Tooltip, Table, TableHead,
  TableRow, TableCell, Tabs, Tab, Divider,
} from '@mui/material';
import TableBody from '../components/PagedTableBody'; // pages rows beyond 10
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DirectionsBusIcon from '@mui/icons-material/DirectionsBus';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import TrafficIcon from '@mui/icons-material/Traffic';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import WarningIcon from '@mui/icons-material/Warning';
import {
  MY_CEREMONIES_QUERY,
  MY_GUESTS_QUERY,
  MY_TRANSPORT_REQUESTS_QUERY,
  CREATE_TRANSPORT_REQUEST_MUTATION,
  UPDATE_TRANSPORT_REQUEST_MUTATION,
  DELETE_TRANSPORT_REQUEST_MUTATION,
  MY_TRANSPORT_VEHICLES_QUERY,
  CREATE_TRANSPORT_VEHICLE_MUTATION,
  UPDATE_TRANSPORT_VEHICLE_MUTATION,
  DELETE_TRANSPORT_VEHICLE_MUTATION,
  ASSIGN_VEHICLE_MUTATION,
  UNASSIGN_VEHICLE_MUTATION,
} from '../graphql/queries';

const STATUS_COLOR  = { requested: 'warning', confirmed: 'info', en_route: 'primary', completed: 'success', cancelled: 'error' };
const VSTATUS_COLOR = { available: 'success', dispatched: 'warning', maintenance: 'error' };
const EMPTY_REQ     = { ceremonyId: '', guestId: '', guestName: '', pickupLocation: '', dropLocation: '', pickupTime: '', vehicleType: 'car', passengerCount: 1, notes: '' };
const EMPTY_VEH     = { vehicleNumber: '', vehicleType: 'car', capacity: 4, driverName: '', driverPhone: '', notes: '' };

// Traffic flow time bands
const TIME_BANDS = [
  { key: 'early_morning', label: 'Early Morning', subtitle: 'Before 9AM — Pre-event arrivals', range: [0, 9] },
  { key: 'morning',       label: 'Morning',       subtitle: '9AM–12PM — Ceremony arrivals',  range: [9, 12] },
  { key: 'afternoon',     label: 'Afternoon',     subtitle: '12PM–4PM — Post-ceremony transfers', range: [12, 16] },
  { key: 'evening',       label: 'Evening',       subtitle: '4PM–8PM — Reception arrivals', range: [16, 20] },
  { key: 'night',         label: 'Night',         subtitle: 'After 8PM — Departures',       range: [20, 24] },
  { key: 'no_time',       label: 'Unscheduled',   subtitle: 'No pickup time set',            range: null },
];

function parseHour(timeStr) {
  if (!timeStr) return null;
  const match = timeStr.match(/(\d{1,2}):(\d{2})\s*(AM|PM)?/i);
  if (!match) return null;
  let h = parseInt(match[1]);
  const m = parseInt(match[2]);
  const period = (match[3] || '').toUpperCase();
  if (period === 'PM' && h !== 12) h += 12;
  if (period === 'AM' && h === 12) h = 0;
  return h + m / 60;
}

export default function TransportationPage() {
  const [tab, setTab]       = useState(0);
  const [error, setError]   = useState('');

  // Request tab
  const [reqDialog, setReqDialog] = useState(false);
  const [editReq, setEditReq]     = useState(null);
  const [reqForm, setReqForm]     = useState(EMPTY_REQ);
  const [filterCeremony, setFilterCeremony] = useState('');

  // Fleet tab
  const [vehDialog, setVehDialog] = useState(false);
  const [editVeh, setEditVeh]     = useState(null);
  const [vehForm, setVehForm]     = useState(EMPTY_VEH);

  // Dispatch tab
  const [dispCeremony, setDispCeremony]   = useState('');
  const [assignDialog, setAssignDialog]   = useState(false);
  const [assignReqId, setAssignReqId]     = useState('');
  const [assignVehId, setAssignVehId]     = useState('');

  // Traffic Flow tab
  const [trafficCeremony, setTrafficCeremony] = useState('');

  // Queries
  const { data: ceremonyData } = useQuery(MY_CEREMONIES_QUERY, { fetchPolicy: 'cache-and-network' });
  const { data: guestData }    = useQuery(MY_GUESTS_QUERY,     { fetchPolicy: 'cache-and-network' });
  const { data: reqData, loading: reqLoading, refetch: refetchReqs } = useQuery(MY_TRANSPORT_REQUESTS_QUERY, {
    variables: filterCeremony ? { ceremonyId: filterCeremony } : {},
    fetchPolicy: 'cache-and-network',
  });
  const { data: vehData, loading: vehLoading, refetch: refetchVehs } = useQuery(MY_TRANSPORT_VEHICLES_QUERY, {
    fetchPolicy: 'cache-and-network',
    skip: tab !== 1 && tab !== 2 && tab !== 3,
  });

  const ceremonies    = ceremonyData?.myCeremonies?.filter(c => c.status !== 'cancelled') || [];
  const guests        = guestData?.myGuests || [];
  const requests      = reqData?.myTransportRequests || [];
  const vehicles      = vehData?.myTransportVehicles || [];
  const availableVehs = vehicles.filter(v => v.status === 'available');
  const dispReqs      = dispCeremony ? requests.filter(r => r.ceremonyId === dispCeremony) : requests;

  // Traffic Flow computation
  const trafficReqs = trafficCeremony ? requests.filter(r => r.ceremonyId === trafficCeremony) : requests;
  const trafficBands = TIME_BANDS.map(band => ({
    ...band,
    items: trafficReqs.filter(r => {
      const h = parseHour(r.pickupTime);
      if (band.range === null) return h === null;
      return h !== null && h >= band.range[0] && h < band.range[1];
    }).sort((a, b) => (parseHour(a.pickupTime) || 0) - (parseHour(b.pickupTime) || 0)),
  })).filter(b => b.items.length > 0);
  const trafficUnassigned = trafficReqs.filter(r => !r.assignment && r.status !== 'cancelled').length;
  const trafficPax        = trafficReqs.reduce((sum, r) => sum + (r.passengerCount || 0), 0);
  const summary       = {
    total:     requests.length,
    pending:   requests.filter(r => r.status === 'requested').length,
    confirmed: requests.filter(r => r.status === 'confirmed').length,
    completed: requests.filter(r => r.status === 'completed').length,
  };

  // Request mutations
  const [createReq] = useMutation(CREATE_TRANSPORT_REQUEST_MUTATION, {
    onCompleted: () => { setReqDialog(false); setReqForm(EMPTY_REQ); refetchReqs(); setError(''); },
    onError: e => setError(e.message),
  });
  const [updateReq] = useMutation(UPDATE_TRANSPORT_REQUEST_MUTATION, {
    onCompleted: () => { setReqDialog(false); setEditReq(null); setReqForm(EMPTY_REQ); refetchReqs(); setError(''); },
    onError: e => setError(e.message),
  });
  const [deleteReq] = useMutation(DELETE_TRANSPORT_REQUEST_MUTATION, { onCompleted: () => refetchReqs() });

  const openCreateReq = () => { setEditReq(null); setReqForm(EMPTY_REQ); setReqDialog(true); };
  const openEditReq   = (r) => {
    setEditReq(r);
    setReqForm({ ceremonyId: r.ceremonyId || '', guestId: r.guestId || '', guestName: r.guestName || '', pickupLocation: r.pickupLocation || '', dropLocation: r.dropLocation || '', pickupTime: r.pickupTime || '', vehicleType: r.vehicleType, passengerCount: r.passengerCount, notes: r.notes || '' });
    setReqDialog(true);
  };
  const handleSaveReq = () => {
    const vars = { ...reqForm, passengerCount: Number(reqForm.passengerCount) || 1, ceremonyId: reqForm.ceremonyId || null, guestId: reqForm.guestId || null };
    if (editReq) updateReq({ variables: { id: editReq.id, ...vars } });
    else createReq({ variables: vars });
  };

  // Vehicle mutations
  const [createVeh] = useMutation(CREATE_TRANSPORT_VEHICLE_MUTATION, {
    onCompleted: () => { setVehDialog(false); setVehForm(EMPTY_VEH); refetchVehs(); setError(''); },
    onError: e => setError(e.message),
  });
  const [updateVeh] = useMutation(UPDATE_TRANSPORT_VEHICLE_MUTATION, {
    onCompleted: () => { setVehDialog(false); setEditVeh(null); setVehForm(EMPTY_VEH); refetchVehs(); setError(''); },
    onError: e => setError(e.message),
  });
  const [deleteVeh] = useMutation(DELETE_TRANSPORT_VEHICLE_MUTATION, { onCompleted: () => refetchVehs() });

  const openCreateVeh = () => { setEditVeh(null); setVehForm(EMPTY_VEH); setVehDialog(true); };
  const openEditVeh   = (v) => {
    setEditVeh(v);
    setVehForm({ vehicleNumber: v.vehicleNumber, vehicleType: v.vehicleType, capacity: v.capacity, driverName: v.driverName || '', driverPhone: v.driverPhone || '', notes: v.notes || '' });
    setVehDialog(true);
  };
  const handleSaveVeh = () => {
    const vars = { ...vehForm, capacity: Number(vehForm.capacity) || 4 };
    if (editVeh) updateVeh({ variables: { id: editVeh.id, ...vars } });
    else createVeh({ variables: vars });
  };

  // Dispatch mutations
  const [assignVehicle]   = useMutation(ASSIGN_VEHICLE_MUTATION,   { onCompleted: () => { setAssignDialog(false); refetchReqs(); refetchVehs(); setError(''); }, onError: e => setError(e.message) });
  const [unassignVehicle] = useMutation(UNASSIGN_VEHICLE_MUTATION, { onCompleted: () => { refetchReqs(); refetchVehs(); }, onError: e => setError(e.message) });

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h4" fontWeight={700}>Transportation</Typography>
          <Typography variant="body2" color="text.secondary">Manage guest transport requests, vehicle fleet, and dispatch logistics.</Typography>
        </Box>
        {tab === 0 && <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateReq}>Add Request</Button>}
        {tab === 1 && <Button variant="contained" startIcon={<LocalShippingIcon />} onClick={openCreateVeh}>Add Vehicle</Button>}
      </Stack>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="Requests" />
        <Tab label="Fleet" />
        <Tab label="Dispatch" />
        <Tab label="Traffic Flow Plan" icon={<TrafficIcon />} iconPosition="start" />
      </Tabs>

      {/* TAB 0: Requests */}
      {tab === 0 && (
        <Box>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {[
              { label: 'Total',     value: summary.total },
              { label: 'Pending',   value: summary.pending },
              { label: 'Confirmed', value: summary.confirmed },
              { label: 'Completed', value: summary.completed },
            ].map(s => (
              <Grid item xs={6} sm={3} key={s.label}>
                <Card><CardContent sx={{ textAlign: 'center', py: '12px !important' }}>
                  <Typography variant="h4" fontWeight={700}>{s.value}</Typography>
                  <Typography variant="caption" color="text.secondary">{s.label}</Typography>
                </CardContent></Card>
              </Grid>
            ))}
          </Grid>
          <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap">
            <Chip
              label="All"
              onClick={() => setFilterCeremony('')}
              color={!filterCeremony ? 'primary' : 'default'}
              variant={!filterCeremony ? 'filled' : 'outlined'}
            />
            {ceremonies.map(c => (
              <Chip
                key={c.id}
                label={c.name}
                onClick={() => setFilterCeremony(c.id === filterCeremony ? '' : c.id)}
                color={filterCeremony === c.id ? 'primary' : 'default'}
                variant={filterCeremony === c.id ? 'filled' : 'outlined'}
              />
            ))}
          </Stack>
          {reqLoading && <LinearProgress sx={{ mb: 2 }} />}
          {requests.length === 0 && !reqLoading && <Alert severity="info">No transport requests yet.</Alert>}
          <Table size="small">
            <TableHead>
              <TableRow>
                {['Guest', 'Ceremony', 'Pickup', 'Drop', 'Time', 'Vehicle', 'Passengers', 'Status', 'Actions'].map(h => (
                  <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {requests.map(r => (
                <TableRow key={r.id} hover>
                  <TableCell>{r.guest?.name || r.guestName || '\u2014'}</TableCell>
                  <TableCell>{r.ceremony?.name || '\u2014'}</TableCell>
                  <TableCell sx={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.pickupLocation || '\u2014'}</TableCell>
                  <TableCell sx={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.dropLocation || '\u2014'}</TableCell>
                  <TableCell>{r.pickupTime || '\u2014'}</TableCell>
                  <TableCell><Chip label={r.vehicleType} size="small" icon={<DirectionsBusIcon />} /></TableCell>
                  <TableCell>{r.passengerCount}</TableCell>
                  <TableCell><Chip label={r.status} size="small" color={STATUS_COLOR[r.status] || 'default'} /></TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.25}>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => openEditReq(r)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" color="error" onClick={() => deleteReq({ variables: { id: r.id } })}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      {/* TAB 1: Fleet */}
      {tab === 1 && (
        <Box>
          {vehLoading && <LinearProgress sx={{ mb: 2 }} />}
          {vehicles.length === 0 && !vehLoading && (
            <Alert severity="info">No vehicles registered yet. Click <strong>Add Vehicle</strong> to build your fleet.</Alert>
          )}
          <Table size="small">
            <TableHead>
              <TableRow>
                {['Vehicle No.', 'Type', 'Capacity', 'Driver', 'Driver Phone', 'Status', 'Actions'].map(h => (
                  <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {vehicles.map(v => (
                <TableRow key={v.id} hover>
                  <TableCell><Typography variant="body2" fontWeight={600}>{v.vehicleNumber}</Typography></TableCell>
                  <TableCell><Chip label={v.vehicleType} size="small" icon={<LocalShippingIcon />} /></TableCell>
                  <TableCell>{v.capacity} pax</TableCell>
                  <TableCell>{v.driverName || '\u2014'}</TableCell>
                  <TableCell>{v.driverPhone || '\u2014'}</TableCell>
                  <TableCell><Chip label={v.status} size="small" color={VSTATUS_COLOR[v.status] || 'default'} /></TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.25}>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => openEditVeh(v)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" color="error" onClick={() => deleteVeh({ variables: { id: v.id } })}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      {/* TAB 2: Dispatch */}
      {tab === 2 && (
        <Box>
          <Stack direction="row" spacing={2} sx={{ mb: 2 }} alignItems="center">
            <TextField
              select
              label="Filter by Ceremony"
              value={dispCeremony}
              onChange={e => setDispCeremony(e.target.value)}
              size="small"
              sx={{ minWidth: 220 }}
            >
              <MenuItem value="">All Ceremonies</MenuItem>
              {ceremonies.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
            </TextField>
            <Typography variant="body2" color="text.secondary">{availableVehs.length} vehicle(s) available</Typography>
          </Stack>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Table size="small">
            <TableHead>
              <TableRow>
                {['Guest', 'Pickup', 'Drop', 'Time', 'Vehicle Type', 'Status', 'Assigned Vehicle', 'Actions'].map(h => (
                  <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {dispReqs.map(r => (
                <TableRow key={r.id} hover>
                  <TableCell>{r.guest?.name || r.guestName || '\u2014'}</TableCell>
                  <TableCell sx={{ maxWidth: 110, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.pickupLocation || '\u2014'}</TableCell>
                  <TableCell sx={{ maxWidth: 110, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.dropLocation || '\u2014'}</TableCell>
                  <TableCell>{r.pickupTime || '\u2014'}</TableCell>
                  <TableCell><Chip label={r.vehicleType} size="small" /></TableCell>
                  <TableCell><Chip label={r.status} size="small" color={STATUS_COLOR[r.status] || 'default'} /></TableCell>
                  <TableCell>
                    {r.assignment
                      ? <Chip label={`${r.assignment.vehicle?.vehicleNumber} (${r.assignment.vehicle?.vehicleType})`} size="small" color="primary" />
                      : <Typography variant="caption" color="text.secondary">Unassigned</Typography>
                    }
                  </TableCell>
                  <TableCell>
                    {r.assignment ? (
                      <Tooltip title="Unassign vehicle">
                        <IconButton size="small" color="warning" onClick={() => unassignVehicle({ variables: { id: r.assignment.id } })}>
                          <LinkOffIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Tooltip title="Assign vehicle">
                        <span>
                          <IconButton
                            size="small"
                            color="success"
                            disabled={availableVehs.length === 0}
                            onClick={() => { setAssignReqId(r.id); setAssignVehId(''); setAssignDialog(true); }}
                          >
                            <LinkIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      {/* TAB 3: Traffic Flow Plan */}
      {tab === 3 && (
        <Box>
          {/* Ceremony filter */}
          <Stack direction="row" spacing={1} sx={{ mb: 2 }} alignItems="center" flexWrap="wrap">
            <TrafficIcon color="action" />
            <Chip label="All Ceremonies" onClick={() => setTrafficCeremony('')} color={!trafficCeremony ? 'primary' : 'default'} variant={!trafficCeremony ? 'filled' : 'outlined'} />
            {ceremonies.map(c => (
              <Chip key={c.id} label={c.name} onClick={() => setTrafficCeremony(c.id === trafficCeremony ? '' : c.id)} color={trafficCeremony === c.id ? 'primary' : 'default'} variant={trafficCeremony === c.id ? 'filled' : 'outlined'} />
            ))}
          </Stack>

          {trafficReqs.length === 0 && (
            <Alert severity="info">No transport requests found. Add requests in the <strong>Requests</strong> tab first.</Alert>
          )}

          {trafficReqs.length > 0 && (<>
            {/* Summary cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {[
                { label: 'Total Requests', value: trafficReqs.length, color: 'primary.main' },
                { label: 'Total Passengers', value: trafficPax, color: 'info.main' },
                { label: 'Unassigned', value: trafficUnassigned, color: trafficUnassigned > 0 ? 'error.main' : 'success.main' },
                { label: 'Fleet Available', value: vehicles.filter(v => v.status === 'available').length, color: 'success.main' },
              ].map(s => (
                <Grid item xs={6} sm={3} key={s.label}>
                  <Card>
                    <CardContent sx={{ textAlign: 'center', py: '12px !important' }}>
                      <Typography variant="h4" fontWeight={700} color={s.color}>{s.value}</Typography>
                      <Typography variant="caption" color="text.secondary">{s.label}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {trafficUnassigned > 0 && (
              <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 2 }}>
                <strong>{trafficUnassigned} request(s)</strong> still unassigned. Visit the <strong>Dispatch</strong> tab to assign vehicles.
              </Alert>
            )}

            {/* Timeline bands */}
            {trafficBands.map(band => (
              <Box key={band.key} sx={{ mb: 3 }}>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
                  <AccessTimeIcon color="primary" />
                  <Box>
                    <Typography variant="h6" fontWeight={700}>{band.label}</Typography>
                    <Typography variant="caption" color="text.secondary">{band.subtitle}</Typography>
                  </Box>
                  <Chip label={`${band.items.length} trip${band.items.length !== 1 ? 's' : ''}`} size="small" color="primary" />
                  <Chip
                    label={`${band.items.reduce((s, r) => s + (r.passengerCount || 0), 0)} pax`}
                    size="small"
                    variant="outlined"
                  />
                </Stack>

                <Grid container spacing={1.5}>
                  {band.items.map(r => (
                    <Grid item xs={12} sm={6} md={4} key={r.id}>
                      <Card variant="outlined" sx={{ borderLeft: 4, borderColor: r.assignment ? 'success.main' : (r.status === 'cancelled' ? 'grey.400' : 'warning.main') }}>
                        <CardContent sx={{ py: '10px !important' }}>
                          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Stack direction="row" spacing={0.5} alignItems="center" mb={0.5}>
                                {r.pickupTime && (
                                  <Chip label={r.pickupTime} size="small" color="default" icon={<AccessTimeIcon />} />
                                )}
                                <Chip label={r.vehicleType} size="small" icon={<DirectionsBusIcon />} />
                                <Chip label={`${r.passengerCount} pax`} size="small" variant="outlined" />
                              </Stack>
                              <Typography variant="body2" fontWeight={600} noWrap>
                                {r.guest?.name || r.guestName || 'Guest'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" display="block" noWrap>
                                {r.pickupLocation || '—'} to {r.dropLocation || '—'}
                              </Typography>
                              {r.ceremony && (
                                <Typography variant="caption" color="primary" display="block">{r.ceremony.name}</Typography>
                              )}
                            </Box>
                            <Stack spacing={0.3} alignItems="flex-end" sx={{ ml: 1, flexShrink: 0 }}>
                              <Chip label={r.status} size="small" color={STATUS_COLOR[r.status] || 'default'} />
                              {r.assignment ? (
                                <Chip
                                  label={r.assignment.vehicle?.vehicleNumber || 'Assigned'}
                                  size="small"
                                  color="success"
                                  variant="outlined"
                                />
                              ) : r.status !== 'cancelled' ? (
                                <Chip label="No Vehicle" size="small" color="warning" variant="outlined" icon={<WarningIcon />} />
                              ) : null}
                            </Stack>
                          </Stack>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            ))}

            {/* Vehicle utilisation summary */}
            {vehicles.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" fontWeight={700} gutterBottom>Fleet Utilisation</Typography>
                <Grid container spacing={1.5}>
                  {vehicles.map(v => {
                    const assigned = trafficReqs.filter(r => r.assignment?.vehicle?.id === v.id);
                    return (
                      <Grid item xs={12} sm={6} md={3} key={v.id}>
                        <Card variant="outlined">
                          <CardContent sx={{ py: '10px !important' }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
                              <Typography variant="body2" fontWeight={700}>{v.vehicleNumber}</Typography>
                              <Chip label={v.status} size="small" color={VSTATUS_COLOR[v.status] || 'default'} />
                            </Stack>
                            <Typography variant="caption" color="text.secondary" display="block">{v.vehicleType} · {v.capacity} pax cap</Typography>
                            {v.driverName && <Typography variant="caption" color="text.secondary" display="block">Driver: {v.driverName}</Typography>}
                            <Divider sx={{ my: 0.5 }} />
                            <Typography variant="caption">
                              {assigned.length > 0
                                ? <><strong>{assigned.length}</strong> trip(s) assigned · {assigned.reduce((s, r) => s + (r.passengerCount || 0), 0)} pax</>
                                : <span style={{ color: '#999' }}>No trips assigned</span>
                              }
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            )}
          </>)}
        </Box>
      )}

      {/* Request Dialog */}
      <Dialog open={reqDialog} onClose={() => setReqDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editReq ? 'Edit Transport Request' : 'New Transport Request'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Stack direction="row" spacing={2}>
              <TextField
                select
                label="Ceremony"
                value={reqForm.ceremonyId}
                onChange={e => setReqForm(f => ({ ...f, ceremonyId: e.target.value }))}
                fullWidth
              >
                <MenuItem value="">None</MenuItem>
                {ceremonies.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
              </TextField>
              <TextField
                select
                label="Guest"
                value={reqForm.guestId}
                onChange={e => setReqForm(f => ({
                  ...f,
                  guestId: e.target.value,
                  guestName: guests.find(g => g.id === e.target.value)?.name || f.guestName,
                }))}
                fullWidth
              >
                <MenuItem value="">Type name below</MenuItem>
                {guests.map(g => <MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>)}
              </TextField>
            </Stack>
            {!reqForm.guestId && (
              <TextField
                label="Guest Name"
                value={reqForm.guestName}
                onChange={e => setReqForm(f => ({ ...f, guestName: e.target.value }))}
                fullWidth
              />
            )}
            <Stack direction="row" spacing={2}>
              <TextField label="Pickup Location" value={reqForm.pickupLocation} onChange={e => setReqForm(f => ({ ...f, pickupLocation: e.target.value }))} fullWidth />
              <TextField label="Drop Location"   value={reqForm.dropLocation}   onChange={e => setReqForm(f => ({ ...f, dropLocation: e.target.value }))}   fullWidth />
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField label="Pickup Time" value={reqForm.pickupTime} onChange={e => setReqForm(f => ({ ...f, pickupTime: e.target.value }))} placeholder="08:30 AM" fullWidth />
              <TextField
                select
                label="Vehicle Type"
                value={reqForm.vehicleType}
                onChange={e => setReqForm(f => ({ ...f, vehicleType: e.target.value }))}
                fullWidth
              >
                {['car', 'suv', 'bus', 'auto', 'taxi', 'valet'].map(v => (
                  <MenuItem key={v} value={v}>{v.charAt(0).toUpperCase() + v.slice(1)}</MenuItem>
                ))}
              </TextField>
              <TextField
                label="Passengers"
                type="number"
                value={reqForm.passengerCount}
                onChange={e => setReqForm(f => ({ ...f, passengerCount: e.target.value }))}
                fullWidth
                inputProps={{ min: 1 }}
              />
            </Stack>
            <TextField label="Notes" value={reqForm.notes} onChange={e => setReqForm(f => ({ ...f, notes: e.target.value }))} fullWidth multiline rows={2} />
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setReqDialog(false); setError(''); }}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveReq}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Vehicle Dialog */}
      <Dialog open={vehDialog} onClose={() => setVehDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editVeh ? 'Edit Vehicle' : 'Add Vehicle to Fleet'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Stack direction="row" spacing={2}>
              <TextField label="Vehicle Number" value={vehForm.vehicleNumber} onChange={e => setVehForm(f => ({ ...f, vehicleNumber: e.target.value }))} placeholder="TN01AB1234" fullWidth required />
              <TextField
                select
                label="Vehicle Type"
                value={vehForm.vehicleType}
                onChange={e => setVehForm(f => ({ ...f, vehicleType: e.target.value }))}
                fullWidth
              >
                {['car', 'suv', 'bus', 'auto', 'taxi', 'minibus', 'valet'].map(v => (
                  <MenuItem key={v} value={v}>{v.charAt(0).toUpperCase() + v.slice(1)}</MenuItem>
                ))}
              </TextField>
              <TextField label="Capacity" type="number" value={vehForm.capacity} onChange={e => setVehForm(f => ({ ...f, capacity: e.target.value }))} fullWidth inputProps={{ min: 1 }} />
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField label="Driver Name"  value={vehForm.driverName}  onChange={e => setVehForm(f => ({ ...f, driverName: e.target.value }))}  fullWidth />
              <TextField label="Driver Phone" value={vehForm.driverPhone} onChange={e => setVehForm(f => ({ ...f, driverPhone: e.target.value }))} fullWidth />
            </Stack>
            <TextField label="Notes" value={vehForm.notes} onChange={e => setVehForm(f => ({ ...f, notes: e.target.value }))} fullWidth multiline rows={2} />
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setVehDialog(false); setError(''); }}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveVeh}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Assign Vehicle Dialog */}
      <Dialog open={assignDialog} onClose={() => setAssignDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Assign Vehicle to Request</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              select
              label="Select Vehicle"
              value={assignVehId}
              onChange={e => setAssignVehId(e.target.value)}
              fullWidth
            >
              <MenuItem value="">Choose a vehicle</MenuItem>
              {availableVehs.map(v => (
                <MenuItem key={v.id} value={v.id}>
                  {v.vehicleNumber} \u2014 {v.vehicleType} ({v.capacity} pax){v.driverName ? ` \u00b7 ${v.driverName}` : ''}
                </MenuItem>
              ))}
            </TextField>
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setAssignDialog(false); setError(''); }}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!assignVehId}
            onClick={() => assignVehicle({ variables: { vehicleId: assignVehId, requestId: assignReqId } })}
          >
            Assign
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
